import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { SESSIONS, ACTIVITY_LABEL, formatTime, SHARE_SCOPES, ShareScope, Session } from "../mockData";
import { StatusBadge } from "../components/StatusBadge";
import { AngleChart } from "../components/AngleChart";
import { PhaseTimeline } from "../components/PhaseTimeline";
import { useApiResource, useApi } from "../api/useApi";
import { API_BASE, UPLOADS_BASE } from "../api/base";
import {
  getAnglesTab,
  getMetricsTab,
  getOverlayBundle,
  getPPositionFrames,
  getFreeFeedback,
  frameThumbUrl,
  getUploadStatus,
  deleteUpload,
  relabelUpload,
  upgradeUploadToPro,
  rerenderUpload,
  getMediaStatus,
  getUploadFrames,
  request3dModel,
  searchContacts,
  createAthlete,
  setUploadSubject,
  setUploadTier,
  setUploadEvent,
  searchEvents,
  setUploadEquipment,
  suggestEquipment,
  setUploadUserTags,
  suggestUserTags,
  sceneStartFor,
  getClientPose,
  getLiveSeries,
  sourceClipUrl,
  updateSportLabel,
} from "../api/resources";
import { SPORTS } from "./Upload";
import SportPicker from "../components/SportPicker";
import { NATIVE_RATE_SPORT_IDS } from "../pose/captureCadence";
import LiveFreeAnalysis from "../components/LiveFreeAnalysis";
import { positionsFromBundle } from "../live/positionsFromBundle";
import { cueRowsFromBundle, cueStillFor } from "../live/cueRows";
import { dwellMomentsFrom, captureFpsFor, LEGACY_FPS } from "../live/liveAnalysis";
import { TierBadge, TIER_LABELS, assignableTiers } from "../components/TierBadge";
import type { ContactSearchItem } from "../api/resources";
import { subscriptionLevelForEmail, SubscriptionLevel, canSelectPremium } from "../subscription";
import { clientLabelDecision } from "../pose/clientLabelPolicy";
import ProcessingJourney from "../components/ProcessingJourney";
import PoseClip from "../components/PoseClip";
import { sessionJourney, pollEveryMs } from "../sessionJourney";
import ClientLabellingStep from "../components/ClientLabellingStep";
import type { OverlayPayload } from "../api/resources";
import { uploadToSession } from "../api/adapters";
import type { MetricCardData, MetricChartData } from "../api/types";
import { LiveAngleChart } from "../components/LiveAngleChart";
import {
  ArrowLeft,
  Download,
  Share2,
  Sparkles,
  Play,
  ChevronRight,
  RefreshCw,
  Film,
  Check,
  Loader2,
  X,
  Copy,
  Mail,
  Trash2,
  Link as LinkIcon,
  UserRound,
  UserPlus,
  Search,
  Medal,
  CalendarDays,
  Wrench,
  Tag,
} from "lucide-react";

// Dynamic session page (user, 2026-09-17: the page should reload and re-label after the click,
// with a poll rather than a blocking alert and a manual reload). The page polls while the upload
// is in flux and for a while after any admin action, and refetches the ready view's data the
// moment the analysis comes back.
const POLL_MS = 4000;
const ACTIVE_WATCH_MS = 60_000;

// Default version suffix on the overview feedback MP4 filename so a freshly
// re-rendered video isn't served from a stale CDN/browser cache. Bump on each
// upissue. Can be overridden per-session via the /sessions/:id/:version URL param.
// Superseded by the "-latest.mp4" default; URL /sessions/:id/:version still pins old renders.

function TikTokIcon({ className = "" }: { className?: string }) {
  // lucide-react ships no TikTok glyph, so use a minimal brand mark.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.46v2.62a8 8 0 0 1-4.5-1.43v6.1a6.2 6.2 0 1 1-6.2-6.2c.31 0 .61.02.9.07v2.71a3.5 3.5 0 1 0 2.4 3.32V3h2.4z" />
    </svg>
  );
}

function fmtClock(s: number): string {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const total = Math.floor(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function RetryBlock({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-sm text-slate-500">
      <div className="text-xs">{label}</div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition"
      >
        <RefreshCw className="h-3.5 w-3.5" /> Retry
      </button>
    </div>
  );
}

export default function SessionDetail() {
  const { id, version } = useParams();
  const { user } = useAuth0();
  const navigate = useNavigate();
  const apiFetch = useApi();
  const [phase, setPhase] = useState("P4");
  const [scope] = useState<ShareScope>("private");
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tiktokBusy, setTiktokBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // For real uploads, track which detected phase is selected. "overview" plays
  // the full feedback video; any P-position pins that frame's image instead.
  const [selectedPhase, setSelectedPhase] = useState<"overview" | string>("overview");
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [viewMode, setViewMode] = useState<"video" | "image">("video");
  // Coaching panel: "all" shows every feedback across the upload, "phase"
  // shows only the active P-position. Decoupled from selectedPhase so the
  // user can browse all coaching while the video/body-measures track a phase.
  const [coachingMode, setCoachingMode] = useState<"all" | "phase">("all");
  // The src is normally the upload's overview video. Clicking a coaching row
  // can swap it to a per-feedback video URL emitted by the renderer.
  const [activeFeedbackVideoUrl, setActiveFeedbackVideoUrl] = useState<string | null>(null);
  const [activeCoachingKey, setActiveCoachingKey] = useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  // Numeric id → real upload from /platform/uploads1; otherwise fall back to mockData.
  const uploadIdNum = id != null && /^\d+$/.test(id) ? Number(id) : null;

  // Delete the whole upload (video + all associated data) via the DeleteUpload endpoint,
  // then return to the sessions list. Only enabled for real (numeric) uploads.
  const onDelete = useCallback(async () => {
    if (uploadIdNum == null || deleting) return;
    if (!window.confirm("Delete this session and all of its data? This can't be undone.")) return;
    setDeleting(true);
    try {
      await deleteUpload(apiFetch, uploadIdNum);
      navigate("/sessions");
    } catch {
      setDeleting(false);
      window.alert("Couldn't delete this session. Please try again.");
    }
  }, [apiFetch, uploadIdNum, deleting, navigate]);

  // ADMIN: re-label this upload on the GPU. Archives the client keypoints file aside and
  // re-queues the label service orders — the AutoLabeller re-processes the existing frames.
  const isAdmin = subscriptionLevelForEmail(user?.email) === SubscriptionLevel.ADMIN;

  // After an admin action (Re-Label / Re-Render / Upgrade / a labels submission) the server's
  // state changes over the next seconds to minutes: orders reset, the frame engine flips, a
  // render lands. The page watches the upload until this instant so those changes show up by
  // themselves; the poll below reads it.
  const activeUntilRef = useRef(0);
  const watchForAWhile = useCallback(() => {
    activeUntilRef.current = Date.now() + ACTIVE_WATCH_MS;
  }, []);

  const { data: uploadsResp, isLoading: loadingUploads, reload: reloadUploads } = useApiResource(
    (apiFetch) => (uploadIdNum != null ? getUploadStatus(apiFetch, uploadIdNum) : Promise.resolve(null)),
    [uploadIdNum],
  );

  // ADMIN: re-render this upload's overview video from its EXISTING labels — re-queues only
  // the video-generation order; the render sweep picks it up on its next poll.
  const [rerendering, setRerendering] = useState(false);
  const onRerender = useCallback(async () => {
    if (uploadIdNum == null || rerendering) return;
    if (!window.confirm("Re-render this session's video from the existing labels?")) return;
    setRerendering(true);
    try {
      await rerenderUpload(apiFetch, uploadIdNum);
      watchForAWhile();
      reloadUploads();
      window.alert("Re-render queued — the render sweep will pick it up on its next poll.");
    } catch {
      window.alert("Couldn't queue the re-render. Please try again.");
    } finally {
      setRerendering(false);
    }
  }, [apiFetch, uploadIdNum, rerendering, watchForAWhile, reloadUploads]);

  // Default: the stable "-latest.mp4" the sweep maintains — no version constant to chase.
  // Optional URL override /sessions/:id/:version ("79" or "v79") still pins an exact
  // versioned file for shared links / debugging old renders.
  const versionNum = (() => {
    const m = (version ?? "").match(/^v?(\d+)$/i);
    return m ? m[1] : "latest";
  })();
  const videoVersion = versionNum === "latest" ? "latest" : `v${versionNum}`;
  const shareUrl =
    typeof window === "undefined"
      ? ""
      : id != null
      ? `${window.location.origin}/sessions/${id}/${versionNum}`
      : window.location.href;

  // The matching upload item from the list — drives the processing-state UI
  // and the polling cadence. We pick this off the list rather than hitting a
  // dedicated status endpoint because /platform/uploads1 already returns
  // analysisPercentage per upload (UploadStatusItem).
  const uploadItem = useMemo(
    () =>
      uploadIdNum != null
        ? uploadsResp?.values?.find((u) => u.uploadId === uploadIdNum)
        : undefined,
    [uploadsResp, uploadIdNum],
  );
  const analysisPct = uploadItem?.analysisPercentage ?? 0;

  // Subject tagging: WHO is in the video. The backend already resolves the tag with an
  // uploader fallback into subjectName; a just-applied tag is reflected immediately via a
  // local override (then reloadUploads re-syncs from the server without a page reload).
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [subjectOverride, setSubjectOverride] = useState<{ contactId: number; name: string } | null>(null);
  const onSubjectTagged = useCallback(
    (contactId: number, name: string) => {
      setSubjectOverride({ contactId, name });
      reloadUploads();
    },
    [reloadUploads],
  );

  // Upload tags (athlete-rankings): tier / event / equipment ride the same override
  // pattern as the subject - a just-applied tag shows immediately, reloadUploads
  // re-syncs from the server. `undefined` = no local override yet.
  const [tierMenuOpen, setTierMenuOpen] = useState(false);
  const [tierBusy, setTierBusy] = useState(false);
  const [tierOverride, setTierOverride] = useState<string | null | undefined>(undefined);
  const currentTier = tierOverride !== undefined ? tierOverride : uploadItem?.athleteTier ?? null;
  // The tier belongs to the SUBJECT athlete; encourage subject-first by only offering
  // the control once a subject is resolvable (the uploader fallback counts - they ARE
  // the subject then).
  const subjectResolvable = (subjectOverride?.contactId ?? uploadItem?.subjectContactId) != null;
  const onSetTier = useCallback(
    async (tier: string) => {
      if (uploadIdNum == null || tierBusy) return;
      setTierBusy(true);
      try {
        await setUploadTier(apiFetch, uploadIdNum, tier);
        setTierOverride(tier === "" ? null : tier);
        setTierMenuOpen(false);
        reloadUploads();
      } catch {
        window.alert("Couldn't set the tier. Please try again.");
      } finally {
        setTierBusy(false);
      }
    },
    [apiFetch, uploadIdNum, tierBusy, reloadUploads],
  );

  // ── Correcting a mis-picked technique (owner, 2026-09-20: "I uploaded a deadlift as a squat
  // technique"). The server writes a NEW sportId row (newest wins) and re-queues the analysis, so
  // the page's positions, cues and score come back as the corrected technique.
  const [techniqueMenuOpen, setTechniqueMenuOpen] = useState(false);
  const [techniqueBusy, setTechniqueBusy] = useState(false);
  const currentSportId = useMemo(() => {
    const raw = uploadItem?.sport;
    const n = typeof raw === "string" ? Number(raw) : raw;
    return typeof n === "number" && Number.isFinite(n) ? n : null;
  }, [uploadItem]);
  /** The technique the upload is set to now, for the dialog to name (owner, 2026-09-20: "the
   *  change technique modal should show what its currently set to"). */
  const currentSport = useMemo(
    () => SPORTS.find((s) => s.id === currentSportId) ?? null,
    [currentSportId],
  );
  // EVERY technique is listed. The splitter sampled this clip either every 100 ms or at its native
  // rate, and frames sampled for one cannot be read as the other, so the server refuses a change
  // across that line — but hiding those techniques read as "we don't have that one" (owner,
  // 2026-09-20: "i should be able to change technique to any movement type, right now it just
  // shows some of them?"). They stay on screen, disabled, saying why. Same cadence rule, one
  // source: captureCadence.
  const techniqueOptions = useMemo(() => {
    if (currentSportId == null) return [];
    const nativeNow = NATIVE_RATE_SPORT_IDS.has(currentSportId);
    const rows = SPORTS.filter((s) => s.available).map((s) => {
      if (s.id === currentSportId) return { ...s, available: false, note: "Current technique" };
      if (NATIVE_RATE_SPORT_IDS.has(s.id) !== nativeNow) {
        return { ...s, available: false, note: "Needs a different frame rate — re-upload to use it" };
      }
      return s;
    });
    // Choosable first, so a long tail of unavailable techniques never buries the real options.
    return [...rows.filter((s) => s.available), ...rows.filter((s) => !s.available)];
  }, [currentSportId]);
  /** Whether there is anything to pick — the button is dead without it, and a list of entirely
   *  disabled options is not something to open. */
  const techniqueChoosable = useMemo(
    () => techniqueOptions.filter((s) => s.available).length,
    [techniqueOptions],
  );
  const onChangeTechnique = useCallback(
    async (sportId: number) => {
      if (uploadIdNum == null || techniqueBusy) return;
      setTechniqueBusy(true);
      try {
        await updateSportLabel(apiFetch, { uploadId: uploadIdNum, SportId: sportId });
        setTechniqueMenuOpen(false);
        reloadUploads();
      } catch {
        window.alert("Couldn't change the technique. Please try again.");
      } finally {
        setTechniqueBusy(false);
      }
    },
    [apiFetch, uploadIdNum, techniqueBusy, reloadUploads],
  );

  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventOverride, setEventOverride] =
    useState<{ name: string | null; date: string | null } | undefined>(undefined);
  const currentEventName = eventOverride !== undefined ? eventOverride.name : uploadItem?.eventName ?? null;
  const currentEventDate = eventOverride !== undefined ? eventOverride.date : uploadItem?.eventDate ?? null;
  const onEventTagged = useCallback(
    (name: string | null, date: string | null) => {
      setEventOverride({ name, date });
      reloadUploads();
    },
    [reloadUploads],
  );

  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [equipmentOverride, setEquipmentOverride] =
    useState<{ type: string | null; brand: string | null; spec: string | null } | undefined>(undefined);
  const currentEquipment = equipmentOverride !== undefined
    ? equipmentOverride
    : {
        type: uploadItem?.equipmentType ?? null,
        brand: uploadItem?.equipmentBrand ?? null,
        spec: uploadItem?.equipmentSpec ?? null,
      };
  const equipmentSummary = [currentEquipment.type, currentEquipment.brand, currentEquipment.spec]
    .filter((x): x is string => !!x)
    .join(" · ");
  const onEquipmentTagged = useCallback(
    (type: string | null, brand: string | null, spec: string | null) => {
      setEquipmentOverride({ type, brand, spec });
      reloadUploads();
    },
    [reloadUploads],
  );

  // Free-form user tags (upload-user-tags): the whole set saves on every chip
  // add/remove (uploadSetUserTags replaces the row), riding the same
  // override-then-resync pattern as the other tag rows.
  const [userTagsOverride, setUserTagsOverride] = useState<string[] | undefined>(undefined);
  const currentUserTags = userTagsOverride !== undefined ? userTagsOverride : uploadItem?.userTags ?? [];
  const onUserTagsSaved = useCallback(
    (tags: string[]) => {
      setUserTagsOverride(tags);
      reloadUploads();
    },
    [reloadUploads],
  );

  // Video | 3D media toggle (task #25). The probe is cheap (File.Exists server-side);
  // the 3D viewer is LAZY — nothing 3D loads until the tab is first opened, and it stays
  // mounted afterwards so toggling back is instant. The tab is ALWAYS visible: opening it
  // without a model requests generation and polls until the mesh worker publishes it.
  const { data: mediaStatus, reload: reloadMediaStatus } = useApiResource(
    (apiFetch) => (uploadIdNum != null ? getMediaStatus(apiFetch, uploadIdNum) : Promise.resolve(null)),
    [uploadIdNum],
  );
  const has3d = !!mediaStatus?.model3d;

  // analysisPercentage tracks LABELLING only. It hits 100 the moment the labeller finishes, but
  // the overview video is rendered afterwards by a separate sweep — so treating 100 as "ready"
  // dropped you into the detail view with a player that had nothing to play. Readiness is
  // therefore labelling AND the rendered video existing. Until both hold we stay on the progress
  // card, whose bar already clamps to 99% (see pctDisplay): labelling is done, the render is not,
  // and 99 is the honest number for that state.
  const isLabellingComplete = analysisPct >= 100;
  const hasVideo = !!mediaStatus?.video;
  // Free-tier uploads render NO video (analysis-only): the overlay manifest is their
  // ready signal, and the page opens on the Free tab instead of waiting for an mp4.
  const hasAnalysis = !!mediaStatus?.analysis;
  // On-device first, premium on top. An upload that asked for premium gets its on-device result
  // first; the server then re-labels on the cloud GPU and renders the narrated video. While that
  // runs the label percentage drops back below 100 — and the on-device result must STAY on screen,
  // with premium's progress beside it on the Premium tab, rather than the page falling back to the
  // processing card. (An API from before `premiumRequested` is read by its old signal, the pending
  // video order.)
  const [upgradeQueued, setUpgradeQueued] = useState(false);
  const premiumRequested = upgradeQueued || (mediaStatus?.premiumRequested ?? !!uploadItem?.videoOrderPending);
  const premiumInProgress = premiumRequested && hasAnalysis && !hasVideo;
  const isAnalysisReady = (isLabellingComplete && (hasVideo || hasAnalysis)) || premiumInProgress;

  const [mediaTab, setMediaTab] = useState<"free" | "video" | "3d">("video");
  // Free-tab frame selection: which P-position still is enlarged. Defaults to the
  // first detected position once positions load.
  const [freeFrameLabel, setFreeFrameLabel] = useState<string | null>(null);
  // The cue shown for the selected position (a row key); null = that position's first cue.
  const [freeCueKey, setFreeCueKey] = useState<string | null>(null);
  // A free (analysis-only) upload has no video: land on the Free tab once, when the
  // media status first says so. Never fires for uploads with a video.
  const landedOnFree = useRef(false);
  useEffect(() => {
    if (landedOnFree.current || mediaStatus == null) return;
    if (!mediaStatus.video && mediaStatus.analysis) {
      landedOnFree.current = true;
      setMediaTab("free");
    }
  }, [mediaStatus]);
  // Upgrade a free upload to the premium analysis (GPU labels + narrated video). Optimistic
  // "queued" after the POST; the uploads list reports videoOrderPending on later reads.
  const canUpgrade = canSelectPremium(subscriptionLevelForEmail(user?.email));
  const [upgrading, setUpgrading] = useState(false);
  const onUpgrade = useCallback(async () => {
    if (uploadIdNum == null) return;
    if (!window.confirm("Queue the premium analysis for this upload? GPU labelling and the narrated video take several minutes.")) return;
    setUpgrading(true);
    try {
      const res = await upgradeUploadToPro(apiFetch, uploadIdNum);
      if (res?.func_status !== 1) throw new Error("upgrade request rejected");
      setUpgradeQueued(true);
      watchForAWhile();
      reloadUploads();
    } catch {
      window.alert("Could not queue the premium analysis. Please try again.");
    } finally {
      setUpgrading(false);
    }
  }, [apiFetch, uploadIdNum, reloadUploads, watchForAWhile]);
  // On-device labelling: an upload nothing has claimed can have its split frames labelled in the
  // browser — fetched while the upload is unfinished. The Free tab needs the same ordered frame
  // list on a READY session too: the live analysis view maps each position's frame to playback
  // time via its index in this list (frames pair by index at the split cadence).
  const { data: uploadFrames, reload: reloadUploadFrames } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null && (!isAnalysisReady || mediaTab === "free" || premiumInProgress)
        ? getUploadFrames(apiFetch, uploadIdNum)
        : Promise.resolve(null),
    [uploadIdNum, isAnalysisReady, mediaTab, premiumInProgress],
  );
  const framesToLabel = uploadFrames?.values ?? [];
  // Two consecutive polls agreeing on the frame count = the split's label orders have landed.
  // Labelling off a list still filling in analysed 60 of ~67 frames on 17816.
  const prevFrameCountRef = useRef<number>(-1);
  const [frameListStable, setFrameListStable] = useState(false);
  useEffect(() => {
    const n = framesToLabel.length;
    setFrameListStable(n > 0 && prevFrameCountRef.current === n);
    prevFrameCountRef.current = n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFrames]);
  const [relabeling, setRelabeling] = useState(false);
  const onRelabel = useCallback(async () => {
    if (uploadIdNum == null || relabeling) return;
    if (!window.confirm("Re-label this upload? A free upload is re-extracted on this device (keep the page open until it finishes); a premium upload re-runs on the GPU. Existing labels are replaced.")) return;
    setRelabeling(true);
    try {
      await relabelUpload(apiFetch, uploadIdNum);
      // ONE re-label flow (user, 2026-09-17): the orders are pending again. A free upload's
      // pose is re-extracted on THIS device — the page reloads, sees an upload nothing has
      // claimed and runs the on-device labelling itself; a premium upload re-runs on the GPU.
      // No dialog: the page is the feedback. The reloads flip it to the processing view and
      // the poll then follows the labeller until the analysis is ready again.
      watchForAWhile();
      reloadUploads();
      reloadMediaStatus();
      reloadUploadFrames();
    } catch {
      window.alert("Couldn't queue the re-label. Please try again.");
    } finally {
      setRelabeling(false);
    }
  }, [apiFetch, uploadIdNum, relabeling, reloadUploads, reloadMediaStatus, reloadUploadFrames, watchForAWhile]);

  const onClientLabelsSubmitted = useCallback(() => {
    // The labeller picks the pose file up on its next poll; the polling below shows it.
    watchForAWhile();
    reloadUploadFrames();
    reloadMediaStatus();
    reloadUploads();
  }, [reloadUploadFrames, reloadMediaStatus, reloadUploads, watchForAWhile]);

  const [threeDActivated, setThreeDActivated] = useState(false);
  const open3dTab = useCallback(() => {
    setMediaTab("3d");
    if (uploadIdNum == null) return;
    if (!has3d) {
      // Fire-and-forget: the endpoint is idempotent, the poll below picks up completion.
      request3dModel(apiFetch, uploadIdNum).catch(() => {});
    }
  }, [apiFetch, uploadIdNum, has3d]);
  // Mount the viewer the first time the 3D tab is open AND the model exists (covers both
  // "already built" and "just finished generating while the tab was open").
  useEffect(() => {
    if (mediaTab === "3d" && has3d) setThreeDActivated(true);
  }, [mediaTab, has3d]);
  // While the 3D tab is open without a model, poll for the worker finishing.
  useEffect(() => {
    if (mediaTab !== "3d" || has3d || uploadIdNum == null) return;
    const t = window.setInterval(() => reloadMediaStatus(), 10000);
    return () => window.clearInterval(t);
  }, [mediaTab, has3d, uploadIdNum, reloadMediaStatus]);
  // Latest plays through the API endpoint (range-enabled); a pinned /sessions/:id/:version
  // URL still addresses the exact versioned file directly.
  const overviewVideoUrl =
    videoVersion === "latest"
      ? `${API_BASE}/uploads/getFeedbackVideo?sfcId=${uploadIdNum}`
      : `${UPLOADS_BASE}${uploadIdNum}-overview-feedback-${videoVersion}.mp4`;

  // Poll every 4 s while this upload is in flux: still being processed, a video order pending
  // (an upgrade or re-render waiting for the sweep), or inside the watch window after an admin
  // action. BOTH sources are refreshed: the uploads list carries labelling progress and the
  // pending order, and mediaStatus tells us when the sweep has published the video. Polling only
  // the list would sit at 99% forever once labelling finished, because nothing would ever
  // observe the render landing. Quiet once nothing is in flight.
  const videoOrderPending = upgradeQueued || !!uploadItem?.videoOrderPending;
  // Whether THIS page should label the split frames itself (nothing has claimed the upload).
  const labelOnThisDevice =
    clientLabelDecision({
      analysisPercentage: analysisPct,
      analysisEngine: uploadFrames?.analysisEngine,
      frameCount: framesToLabel.length,
      isAnalysisReady,
      frameListStable,
    }) === "auto";
  // What is happening to the upload, step by step — the processing card and the Premium tab both
  // show this one journey.
  const journey = useMemo(
    () =>
      sessionJourney({
        frames: framesToLabel,
        hasAnalysis,
        hasVideo,
        premiumRequested,
        analysisEngine: mediaStatus?.analysisEngine ?? uploadFrames?.analysisEngine,
        renderStage: mediaStatus?.renderStage,
        labellingOnThisDevice: labelOnThisDevice,
        labellingComplete: isLabellingComplete,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadFrames, mediaStatus, hasAnalysis, hasVideo, premiumRequested, labelOnThisDevice, isLabellingComplete],
  );
  const premiumPollMs = premiumInProgress ? pollEveryMs(journey) : null;
  const lastPollRef = useRef(0);
  useEffect(() => {
    if (uploadIdNum == null) return;
    const t = window.setInterval(() => {
      // Premium on its way keeps the page polling until the video exists, so the Premium tab
      // updates by itself and the video appears without a refresh.
      const inFlux = !isAnalysisReady || videoOrderPending || premiumInProgress || Date.now() < activeUntilRef.current;
      if (!inFlux) return;
      // Nobody is looking: ask again when the tab is back.
      if (typeof document !== "undefined" && document.hidden) return;
      // The render's stages last minutes: while premium is rendering, ask about every 10 s.
      if (premiumPollMs != null && Date.now() - lastPollRef.current < premiumPollMs - 500) return;
      lastPollRef.current = Date.now();
      reloadUploads();
      // The frame strip fills in as each frame's label orders confirm.
      if (!isLabellingComplete) reloadUploadFrames();
      reloadMediaStatus();
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [uploadIdNum, isAnalysisReady, isLabellingComplete, videoOrderPending, premiumInProgress, premiumPollMs, reloadUploads, reloadMediaStatus, reloadUploadFrames]);
  const {
    data: metricsTab,
    isLoading: loadingMetrics,
    error: metricsError,
    reload: reloadMetrics,
  } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null
        ? getMetricsTab(apiFetch, uploadIdNum)
        : Promise.resolve(null),
    [uploadIdNum],
  );
  const { data: ppositions, reload: reloadPPositions } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null
        ? getPPositionFrames(apiFetch, uploadIdNum).catch(() => null)
        : Promise.resolve(null),
    [uploadIdNum],
  );
  const {
    data: anglesTab,
    isLoading: loadingAngles,
    error: anglesError,
    reload: reloadAngles,
  } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null
        ? getAnglesTab(apiFetch, uploadIdNum)
        : Promise.resolve(null),
    [uploadIdNum],
  );

  const liveCharts: MetricChartData[] = useMemo(() => {
    if (!anglesTab?.items?.length) return [];
    const seen = new Set<string>();
    const out: MetricChartData[] = [];
    for (const it of anglesTab.items) {
      for (const c of it.chartData ?? []) {
        const key = c.title;
        if (seen.has(key)) continue;
        seen.add(key);
        if ((c.dataPoints ?? []).length > 0) out.push(c);
      }
    }
    return out;
  }, [anglesTab]);

  const { totalScoreCard, liveMetricCards } = useMemo<{
    totalScoreCard: MetricCardData | null;
    liveMetricCards: MetricCardData[];
  }>(() => {
    if (!metricsTab?.items?.length) return { totalScoreCard: null, liveMetricCards: [] };
    // Backend sends a mix of "90%", "7.5/10", and "75 pts". Normalize to %.
    const toPercent = (raw: string): string => {
      if (!raw) return raw;
      const s = raw.trim();
      const slashTen = /^(-?\d+(?:\.\d+)?)\s*\/\s*10\b/.exec(s);
      if (slashTen) return `${Math.round(parseFloat(slashTen[1]) * 10)}%`;
      if (/%$/.test(s)) return s;
      const num = /^(-?\d+(?:\.\d+)?)/.exec(s);
      if (num) return `${Math.round(parseFloat(num[1]))}%`;
      return s;
    };
    const seen = new Set<string>();
    const out: MetricCardData[] = [];
    for (const it of metricsTab.items) {
      for (const rawCard of it.metricCards ?? []) {
        const card: MetricCardData = { ...rawCard, value: toPercent(rawCard.value) };
        const key = `${card.label}|${card.value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(card);
      }
    }
    const score = out.filter((c) => c.grouping === "Score");
    const cards = score.length > 0 ? score : out;
    const total = cards.find((c) => c.label.toLowerCase() === "total score") ?? null;
    return {
      totalScoreCard: total,
      liveMetricCards: cards.filter((c) => c !== total),
    };
  }, [metricsTab]);

  // One-line summary computed from the real scores + coaching rows this page already
  // loaded. Null when there's nothing to summarize — the section hides rather than
  // showing canned copy.
  const analysisSummary = useMemo<string | null>(() => {
    const pct = (v: string): number | null => {
      const m = /(-?\d+(?:\.\d+)?)/.exec(v ?? "");
      return m ? parseFloat(m[1]) : null;
    };
    const parts: string[] = [];
    const total = totalScoreCard ? pct(totalScoreCard.value) : null;
    if (total != null) parts.push(`Scored ${Math.round(total)}/100 overall.`);

    const phaseScores = liveMetricCards
      .map((c) => ({ phase: /^P\d+/i.exec(c.label.trim())?.[0]?.toUpperCase(), value: pct(c.value) }))
      .filter((p): p is { phase: string; value: number } => p.phase != null && p.value != null);
    if (phaseScores.length >= 2) {
      const best = phaseScores.reduce((a, b) => (b.value > a.value ? b : a));
      const worst = phaseScores.reduce((a, b) => (b.value < a.value ? b : a));
      if (best.phase !== worst.phase) {
        parts.push(
          `Strongest at ${best.phase} (${Math.round(best.value)}), most room at ${worst.phase} (${Math.round(worst.value)}).`
        );
      }
    }
    return parts.length > 0 ? parts.join(" ") : null;
  }, [totalScoreCard, liveMetricCards]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy session link:", shareUrl);
    }
  }, [shareUrl]);

  const onExportPdf = useCallback(() => {
    // Per-session PDFs are surfaced via /metrics/videoReportDetailPDF on the
    // backend. For now, link to the canonical report URL the RN app uses.
    if (uploadIdNum != null) {
      window.open(`${UPLOADS_BASE}${uploadIdNum}-report.pdf`, "_blank");
    }
  }, [uploadIdNum]);

  const onShareToTikTok = useCallback(async () => {
    if (uploadIdNum == null) return;
    // Share whichever clip is on screen — a per-feedback video if one is pinned,
    // otherwise the full overview feedback video.
    const videoUrl =
      activeFeedbackVideoUrl ?? overviewVideoUrl;
    const fileName = `eleetz-session-${uploadIdNum}.mp4`;
    setTiktokBusy(true);
    try {
      const res = await fetch(videoUrl);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: blob.type || "video/mp4" });
      // Preferred path (mobile): hand the actual MP4 to the OS share sheet so the
      // user can pick TikTok. TikTok exposes no web URL that pre-loads a video
      // into its composer, so file-sharing is the only way to push the clip.
      const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
      };
      if (nav.canShare?.({ files: [file] })) {
        try {
          await nav.share({
            files: [file],
            title: `Eleetz session #${uploadIdNum}`,
            text: "Check out my swing analysis on Eleetz",
          });
        } catch {
          // User dismissed the share sheet — nothing more to do.
        }
        return;
      }
      // Desktop fallback: download the clip and open TikTok's upload page so the
      // user can drag it straight in.
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      window.open("https://www.tiktok.com/upload", "_blank");
    } catch {
      // CORS/network failure on the blob fetch: open the raw video and TikTok's
      // upload page so the user can still save and post it manually.
      window.open(videoUrl, "_blank");
      window.open("https://www.tiktok.com/upload", "_blank");
    } finally {
      setTiktokBusy(false);
    }
  }, [uploadIdNum, activeFeedbackVideoUrl, overviewVideoUrl]);

  const session: Session | undefined = useMemo(() => {
    if (uploadIdNum != null) {
      const item = uploadsResp?.values?.find((u) => u.uploadId === uploadIdNum);
      if (!item) return undefined;
      return uploadToSession(item, {
        athleteName: user?.name ?? user?.email ?? "You",
        athleteId: user?.sub ?? "self",
      });
    }
    return SESSIONS.find((s) => s.id === id);
  }, [uploadIdNum, uploadsResp, user, id]);

  const isReal = uploadIdNum != null;
  const livePositions =
    ppositions?.positions?.filter((p) => p.fileId != null && p.fileId > 0) ?? [];
  const selectedFileId =
    selectedPhase === "overview"
      ? null
      : livePositions.find((p) => p.label === selectedPhase)?.fileId ?? null;
  // Free tab: ONE request, made when the tab is open. The stills are the cache — the first open
  // of an older clip analyses once and writes them (slow, once), then it is file reads.
  const { data: freeFeedback, isLoading: loadingFreeFeedback, reload: reloadFreeFeedback } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null && mediaTab === "free"
        ? getFreeFeedback(apiFetch, uploadIdNum).catch(() => null)
        : Promise.resolve(null),
    [uploadIdNum, mediaTab],
  );
  const freePositions = freeFeedback?.positions ?? [];
  // The enlarged still — explicit pick, else the first position.
  const freeFrame =
    freePositions.find((p) => p.label === freeFrameLabel) ?? freePositions[0] ?? null;

  // LIVE free analysis (user reference clip, 2026-09-12): when the upload carries a client-pose
  // pose file, the free tab plays the ORIGINAL clip with the skeleton drawn on it and dwells on
  // each position's feedback still — instead of a static frame. No pose file (or any fetch
  // problem) → the static stills view below, unchanged.
  // Also fetched while the upload is still processing: the processing card plays the clip with this
  // skeleton over it (PoseClip), so there is something of the user's own to look at while waiting.
  // Only once the status is KNOWN to be "processing": a finished session opens straight on its
  // result and must not fetch the pose twice.
  const poseForProcessingCard = mediaStatus != null && !isAnalysisReady;
  const { data: livePoseFile, reload: reloadLivePoseFile } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null && (mediaTab === "free" || poseForProcessingCard)
        ? getClientPose(apiFetch, uploadIdNum)
        : Promise.resolve(null),
    [uploadIdNum, mediaTab, poseForProcessingCard],
  );
  // The live metrics: the C# analysis pass's artifact. 404 (not produced yet) → null and the
  // player hides its metric panels — the browser never computes them itself.
  const { data: liveSeries, reload: reloadLiveSeries } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null && mediaTab === "free"
        ? getLiveSeries(apiFetch, uploadIdNum).catch(() => null)
        : Promise.resolve(null),
    [uploadIdNum, mediaTab],
  );
  const liveReady = (livePoseFile?.frames?.length ?? 0) > 0;

  // One bundled fetch per upload — the API aggregates every fileId × overlay
  // class for the upload's P-position frames. We then filter client-side by
  // selectedFileId, so phase switches are instant.
  const {
    data: overlayBundle,
    isLoading: overlaysLoading,
    error: overlaysError,
    reload: reloadOverlays,
  } = useApiResource(
    (apiFetch) =>
      uploadIdNum != null
        ? getOverlayBundle(apiFetch, uploadIdNum)
        : Promise.resolve(null),
    [uploadIdNum],
  );

  // The moment the analysis becomes ready again while the page is open (a re-label cycle
  // finishing, a render landing on a processing upload), refetch what the ready view shows:
  // the live pose file, the free feedback stills, the live series, positions, overlays, metrics
  // and angles. Their own deps do not change on that transition, so they would otherwise keep
  // the pre-action data until a manual reload. The first observation (mount) records, never fires.
  const wasReadyRef = useRef<boolean | null>(null);
  const hadVideoRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (uploadsResp == null || mediaStatus == null) return; // not known yet
    const was = wasReadyRef.current;
    wasReadyRef.current = isAnalysisReady;
    const hadVideo = hadVideoRef.current;
    hadVideoRef.current = hasVideo;
    // Ready again, or premium has just landed on a page that was showing the on-device result:
    // the stills, positions and score are the premium render's now.
    if ((was === false && isAnalysisReady) || (hadVideo === false && hasVideo)) {
      reloadFreeFeedback();
      reloadLivePoseFile();
      reloadLiveSeries();
      reloadPPositions();
      reloadOverlays();
      reloadMetrics();
      reloadAngles();
    }
  }, [uploadsResp, mediaStatus, isAnalysisReady, hasVideo, reloadFreeFeedback, reloadLivePoseFile, reloadLiveSeries, reloadPPositions, reloadOverlays, reloadMetrics, reloadAngles]);

  const overlayPayloads: OverlayPayload[] =
    selectedFileId != null
      ? overlayBundle?.frames.find((f) => f.fileId === selectedFileId)?.overlays ?? []
      : [];

  // The phases the RENDER stamped on its own overlays, preferred over getPPositionFrames: that endpoint
  // re-runs the position finder against the database, which on a few-hundred-frame clip can take minutes
  // and time out — and while it does, the live view has no phases to offer and the rail has no buttons
  // (owner, 2026-09-24). The bundle is a file read and cannot disagree with the still on screen.
  const phasePositions = (() => {
    const stamped = positionsFromBundle(overlayBundle);
    if (stamped.length > 0) return stamped;
    return livePositions
      .filter((p) => p.fileId != null && p.fileId > 0)
      .map((p) => ({ label: p.label, fileId: p.fileId as number }));
  })();

  const liveMoments = liveReady
    ? dwellMomentsFrom(
        phasePositions,
        (uploadFrames?.values ?? []).map((f) => f.fileId),
        captureFpsFor(freeFeedback?.split, livePoseFile),
        (label) => freePositions.find((p) => p.label === label)?.stillUrl ?? null,
        freeFeedback?.split?.startSec ?? 0,
      )
    : [];


  // Seek the <video> to where a frame's coaching scene actually plays. Renders since
  // scene-time stamping landed carry sceneStartSeconds in the manifest — the exact
  // second each overlay scene appears (same walk that syncs narration audio). Old
  // renders fall back to the previous proportional guess: a fileId's position in the
  // frames list scaled to the duration — crude, because scenes have very unequal
  // lengths (holds, narration, slow-mo), which is why rows used to "never line up".
  const seekVideoToFileId = useCallback(
    (fileId: number, overlay?: string | null) => {
      const v = videoRef.current;
      if (!v) return;
      const seek = () => {
        const total = v.duration;
        if (!Number.isFinite(total) || total <= 0) return;
        const known = sceneStartFor(overlayBundle?.sceneStartSeconds, fileId, overlay);
        if (known != null) {
          v.currentTime = Math.min(Math.max(known, 0), Math.max(total - 0.1, 0));
        } else {
          const frames = overlayBundle?.frames ?? [];
          const idx = frames.findIndex((f) => f.fileId === fileId);
          if (idx < 0 || frames.length <= 1) return;
          v.currentTime = (idx / (frames.length - 1)) * total;
        }
        v.play().catch(() => {});
      };
      if (v.readyState >= 1 && Number.isFinite(v.duration) && v.duration > 0) {
        seek();
      } else {
        v.addEventListener("loadedmetadata", seek, { once: true });
      }
    },
    [overlayBundle],
  );

  const jumpToPhase = useCallback(
    (
      label: string,
      fileId?: number | null,
      opts?: { keepCoachingList?: boolean; feedbackVideoUrl?: string | null; overlay?: string | null },
    ) => {
      setSelectedPhase(label);
      setPhase(label);
      setViewMode("video");
      // Default: switch the coaching panel to the per-phase view. When the
      // click came from the full list itself, keep showing the list.
      setCoachingMode(
        opts?.keepCoachingList || label === "overview" ? "all" : "phase",
      );
      // Per-feedback video src: clear unless the caller supplied one.
      setActiveFeedbackVideoUrl(opts?.feedbackVideoUrl ?? null);
      // Clear coaching-row highlight when navigating from anywhere other than
      // a coaching button (those set the key before calling).
      if (opts?.feedbackVideoUrl == null) setActiveCoachingKey(null);
      // Seek only within the overview video. A per-feedback clip (src swap above) is
      // entirely about its feedback and starts at 0 — seeking it by overview scene
      // times (or the old proportional guess) landed nowhere meaningful.
      if (fileId != null && opts?.feedbackVideoUrl == null)
        seekVideoToFileId(fileId, opts?.overlay);
    },
    [seekVideoToFileId],
  );

  // "Analyzing your …" is a CLAIM about the upload, so it may only be made once the page KNOWS.
  // mediaStatus is what says whether a video or an analysis has been published; until it lands,
  // readiness is unknown, not false. Without this the analysing card — progress bar and
  // "rendering your feedback video" copy — rendered on every open of a FINISHED session, for the
  // whole window between the uploads list landing and mediaStatus landing (user, 2026-09-19:
  // "this screen also shows too often because the analysis has already been performed").
  const statusKnown = mediaStatus != null;
  if (uploadIdNum != null && ((!session && loadingUploads) || !statusKnown)) {
    return (
      <div className="card p-12 flex flex-col items-center gap-3 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin text-el-600" />
        <div className="text-sm">Loading session…</div>
      </div>
    );
  }
  if (!session) {
    return (
      <div className="card p-8 text-center text-slate-500">
        Session not found.{" "}
        <Link to="/sessions" className="text-el-700">Back to sessions</Link>
      </div>
    );
  }

  // Worker is still processing this upload — show a progress card and keep polling
  // (the useEffect above re-fetches /platform/uploads1 every 4s until ready).
  if (uploadIdNum != null && !isAnalysisReady) {
    const label = ACTIVITY_LABEL[session.activity] ?? "your video";
    const pctDisplay = Math.max(0, Math.min(99, Math.round(analysisPct)));
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Link to="/sessions" className="btn-secondary !h-8">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="text-sm text-slate-500 truncate">Session #{uploadIdNum}</div>
          {/* An upload that sticks in "Analyzing" is exactly when an admin needs to re-queue it,
              but this branch returns before the ready-state header that carries Re-Label — so the
              one control that unwedges it was unreachable precisely when it was needed. Same
              handler and same ADMIN gate as the ready state; no disabled placeholder for everyone
              else, since a permanently dead button on a transient progress screen is just noise. */}
          <div className="ml-auto flex items-center gap-2">
            {/* Reachable while the upload is still analysing, and by anyone: noticing the wrong
                technique mid-analysis is exactly when it needs correcting, and the person who
                picked it is whoever uploaded it. */}
            <button
              className="btn-secondary !h-8"
              onClick={() => setTechniqueMenuOpen(true)}
              disabled={techniqueBusy || uploadIdNum == null || techniqueChoosable === 0}
              title="Picked the wrong technique when uploading? Change it and the clip is re-analysed."
            >
              {techniqueBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4" />
              )}{" "}
              <span className="hidden sm:inline">Change technique</span>
            </button>
            {isAdmin && (
              <>
              <button
                className="btn-secondary !h-8"
                onClick={onRelabel}
                disabled={relabeling || uploadIdNum == null}
                title="Archive the client keypoints and re-queue this upload for GPU labelling"
              >
                {relabeling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}{" "}
                <span className="hidden sm:inline">Re-Label</span>
              </button>
              {/* Re-Render sits wherever Re-Label does (user direction: "a button for the admin
                  to request a render like re-labelling") — same gate, video order only. */}
              <button
                className="btn-secondary !h-8"
                onClick={onRerender}
                disabled={rerendering || uploadIdNum == null}
                title="Re-render this session's video from the existing labels"
              >
                {rerendering ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Film className="h-4 w-4" />
                )}{" "}
                <span className="hidden sm:inline">Re-Render</span>
              </button>
              </>
            )}
            {/* Delete mirrors the ready-state header's button (owner-level, NOT admin-gated):
                a junk upload stuck at 0% is exactly the one its owner wants rid of, and this
                branch returns before the header that carries Delete. Same handler + confirm. */}
            <button
              className="btn-secondary !h-8 !text-rose-600 !border-rose-200 hover:!bg-rose-50"
              onClick={onDelete}
              disabled={deleting || uploadIdNum == null}
              title="Delete this session and all of its data"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}{" "}
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>
        <div className="card p-8 flex flex-col items-center text-center gap-4">
          <div>
            <div className="text-lg font-semibold text-slate-900">
              Analyzing your {label.toLowerCase()}…
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {journey.premium
                ? "Your on-device result comes first. The cloud analysis and the narrated video follow, and this page updates by itself."
                : "Your key frames, cues and score are on their way. This usually takes a minute or two."}
            </div>
          </div>
          {/* The user's own clip with the pose found on their device, while they wait. */}
          {liveReady && <PoseClip clipUrl={sourceClipUrl(uploadIdNum)} poseFile={livePoseFile!} />}
          {/* Overall labelling progress (confirmed label orders over all of them). */}
          <div className="w-full max-w-md">
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-el-600 transition-all duration-500"
                style={{ width: `${pctDisplay}%` }}
              />
            </div>
            <div className="text-xs text-slate-500 mt-1.5">{pctDisplay}%</div>
          </div>
          {/* The journey, step by step, with the frame count under the step that is counting. */}
          <ProcessingJourney journey={journey} />
          {/* Nothing has claimed this upload and its frames are split: label them here, in the
              browser, rather than waiting on a queue. An upload that already has client labels, or
              whose uploader chose the GPU analysis, is left exactly as it is. */}
          {labelOnThisDevice && (
            <ClientLabellingStep
              uploadId={uploadIdNum}
              frames={framesToLabel}
              captureFps={uploadFrames?.captureFps ?? LEGACY_FPS}
              onSubmitted={onClientLabelsSubmitted}
            />
          )}
          {/* The frames, filling in as their label orders confirm — the Frame view's own
              server-drawn pose image for a labelled frame, the raw frame until then. The bar
              above is the same progress (confirmed orders over all orders). */}
          {framesToLabel.length > 0 && (
            <div className="w-full flex gap-1 overflow-x-auto pt-2" data-testid="labelling-frame-strip">
              {framesToLabel.map((f) => (
                <img
                  key={`${f.fileId}-${f.labelled ? "labelled" : "raw"}`}
                  src={f.labelled ? `${API_BASE}/uploads/getImage?fileIds=${f.fileId}` : frameThumbUrl(f.fileId)}
                  alt={f.labelled ? `frame ${f.fileId} labelled` : `frame ${f.fileId}`}
                  data-testid={f.labelled ? "strip-frame-labelled" : "strip-frame-raw"}
                  className={`h-16 w-auto shrink-0 rounded bg-black object-contain ${f.labelled ? "" : "opacity-40"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const isGolf = session.activity.startsWith("golf");
  // The LIVE free view is open: the media card takes the whole row and the coaching card's
  // content lives in the live column's Coaching tab (user, 2026-09-12).
  const liveOpen = isReal && mediaTab === "free" && liveReady;

  // Per-frame feedback for the currently selected P-position cell.
  const overlayFeedbackRows = overlayPayloads.flatMap((p) =>
    p.shapes
      .filter((s) => s.shapeType === "Text")
      .map((s) => ({
        overlay: p.overlay,
        description:
          s.shapeKeyValues?.outputDescription || s.shapeKeyValues?.text || p.overlay,
        value: s.shapeKeyValues?.outputValue || "",
        units: s.shapeKeyValues?.outputUnits || "",
      }))
      .filter((r) => r.value || r.description),
  );

  // For the Overview view: every text feedback across every frame, tagged with
  // the P-position label so the user can see all coaching at a glance.
  const fileIdToPLabel = new Map<number, string>();
  for (const p of livePositions) {
    if (p.fileId != null) fileIdToPLabel.set(p.fileId, p.label);
  }
  // One row per CUE (an overlay on a position), its readout numbers on the row — not one row per text
  // shape, which split every golf cue into a headline row plus "Shaft 52°"-style readout rows (owner,
  // 2026-09-25). Phase from the renderer's own stamp first, the fileId map as the fallback (live/cueRows).
  const overviewFeedbackRows = cueRowsFromBundle(overlayBundle, fileIdToPLabel);
  // The no-pose-file free panel shows one cue at a time too: the selected position's cues that have their
  // own still, the first by default. None (an upload rendered before cue stills) = the composite.
  const freeCueRows = freeFrame
    ? overviewFeedbackRows.filter(
        (r) => r.pLabel === freeFrame.label && cueStillFor(freeFeedback?.cueStills, r.pLabel, r.overlay)
      )
    : [];
  const freeCue = freeCueRows.find((r) => r.key === freeCueKey) ?? freeCueRows[0] ?? null;
  const freeStillUrl = freeCue
    ? cueStillFor(freeFeedback?.cueStills, freeCue.pLabel, freeCue.overlay)
    : freeFrame?.stillUrl ?? null;

  return (
    <div className="space-y-4">
      {/* breadcrumb / actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <Link to="/sessions" className="inline-flex items-center gap-1 hover:text-slate-700">
            <ArrowLeft className="h-3.5 w-3.5" /> Sessions
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
          <span className="text-slate-700 font-medium">{session.id}</span>
          <StatusBadge status={session.status} />
          {isReal && (
            <span className="inline-flex items-center gap-1.5">
              <UserRound className="h-3.5 w-3.5 text-slate-400" />
              <span>Subject:</span>
              <span className="text-slate-700 font-medium" data-testid="subject-name">
                {subjectOverride?.name ?? uploadItem?.subjectName ?? session.athlete}
              </span>
              <button
                type="button"
                onClick={() => setTagPickerOpen(true)}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition"
                title="Tag who is in this video — a pro/athlete or another user"
              >
                <UserPlus className="h-3 w-3" /> Tag athlete
              </button>
            </span>
          )}
          {isReal && (
            <span className="relative inline-flex items-center gap-1.5">
              <TierBadge tier={currentTier} />
              {subjectResolvable && (
                <button
                  type="button"
                  onClick={() => setTierMenuOpen((o) => !o)}
                  disabled={tierBusy}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition disabled:opacity-50"
                  title="Set the athlete's tier for this clip"
                >
                  <Medal className="h-3 w-3" /> Set tier
                </button>
              )}
              {tierMenuOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[9rem] rounded-md border border-slate-200 bg-white shadow-lg p-1">
                  {/* Tier is captured PER CLIP (tier at the time of the clip) - old clips
                      keep their historical tier when an athlete moves up. Elite Pro is the
                      curated reference tier — assignable by admins only (backend-enforced too). */}
                  {assignableTiers(isAdmin).map((t) => (
                    <button
                      key={t}
                      type="button"
                      disabled={tierBusy}
                      onClick={() => onSetTier(t)}
                      className="w-full text-left rounded px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {TIER_LABELS[t]}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={tierBusy}
                    onClick={() => onSetTier("")}
                    className="w-full text-left rounded px-2.5 py-1.5 text-xs text-slate-500 hover:bg-slate-50 border-t border-slate-100 mt-1 pt-1.5 disabled:opacity-50"
                  >
                    Clear tier
                  </button>
                </div>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ScopePill scope={scope} />
          {/* Correcting a mis-picked technique sits with the other upload actions (owner,
              2026-09-20: "it should be a button like the relabel or rerender button") and is
              NOT admin-only — the person who picked the wrong one is whoever uploaded it. */}
          <button
            className="btn-secondary"
            onClick={() => setTechniqueMenuOpen(true)}
            disabled={techniqueBusy || uploadIdNum == null || techniqueChoosable === 0}
            title="Picked the wrong technique when uploading? Change it and the clip is re-analysed."
          >
            {techniqueBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wrench className="h-4 w-4" />
            )}{" "}
            <span className="hidden sm:inline">Change technique</span>
          </button>
          {isAdmin ? (
            <>
            <button
              className="btn-secondary"
              onClick={onRelabel}
              disabled={relabeling || uploadIdNum == null}
              title="Archive the client keypoints and re-queue this upload for GPU labelling"
            >
              {relabeling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}{" "}
              <span className="hidden sm:inline">Re-Label</span>
            </button>
            <button
              className="btn-secondary"
              onClick={onRerender}
              disabled={rerendering || uploadIdNum == null}
              title="Re-render this session's video from the existing labels"
            >
              {rerendering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Film className="h-4 w-4" />
              )}{" "}
              <span className="hidden sm:inline">Re-Render</span>
            </button>
            </>
          ) : (
            <button
              className="btn-secondary opacity-50 cursor-not-allowed"
              disabled
              title="Coming soon"
            >
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Re-run AI</span>
            </button>
          )}
          <button className="btn-secondary" onClick={() => setShareOpen(true)}>
            <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">Share link</span>
          </button>
          <button className="btn-secondary" onClick={onExportPdf}>
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export PDF</span>
          </button>
          <button
            className="btn-secondary"
            onClick={onShareToTikTok}
            disabled={tiktokBusy || !isReal}
            title="Share this video to TikTok"
          >
            {tiktokBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <TikTokIcon className="h-4 w-4" />
            )}{" "}
            <span className="hidden sm:inline">Share to TikTok</span>
          </button>
          <button
            className="btn-secondary !text-rose-600 !border-rose-200 hover:!bg-rose-50"
            onClick={onDelete}
            disabled={deleting || uploadIdNum == null}
            title="Delete this session and all of its data"
          >
            {deleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}{" "}
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>

      {/* context tags: which event, what equipment (athlete-rankings) */}
      {isReal && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
            <span>Event:</span>
            <span className="text-slate-700 font-medium" data-testid="event-display">
              {currentEventName
                ? currentEventName + (currentEventDate ? " · " + currentEventDate : "")
                : "—"}
            </span>
            <button
              type="button"
              data-testid="event-tag-button"
              onClick={() => setEventDialogOpen(true)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition"
              title="Tag which event (tournament/competition) this clip is from"
            >
              {currentEventName ? "Change" : "Tag event"}
            </button>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Wrench className="h-3.5 w-3.5 text-slate-400" />
            <span>Equipment:</span>
            <span className="text-slate-700 font-medium" data-testid="equipment-display">
              {equipmentSummary || "—"}
            </span>
            <button
              type="button"
              data-testid="equipment-tag-button"
              onClick={() => setEquipmentDialogOpen(true)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition"
              title="Tag the equipment used in this clip"
            >
              {equipmentSummary ? "Change" : "Tag equipment"}
            </button>
          </span>
          {uploadIdNum != null && (
            <UserTagsRow
              uploadId={uploadIdNum}
              apiFetch={apiFetch}
              tags={currentUserTags}
              onSaved={onUserTagsSaved}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* video panel */}
        <div
          className={`${liveOpen ? "lg:col-span-3" : "lg:col-span-2"} card p-0 overflow-hidden flex flex-col`}
          data-testid="media-card"
        >
          {/* While the LIVE free view is open the panel grows to near the viewport height (user,
              2026-09-12: "make the video frame larger like almost full height", then "it
              shouldn't be full full height"). 18rem = app header (4rem) + page padding (1.5rem)
              + the breadcrumb/actions/tags block above this grid (~11rem, measured on the user's
              17816 screenshot) + ~1.5rem so the panel ends just above the fold. Every other tab
              keeps the usual card height. */}
          <div
            className={`bg-slate-900 relative ${
              liveOpen
                ? "shrink-0 min-h-[420px] h-[calc(100vh-18rem)]" // no flex-1: its basis 0 would pin the height at the minimum
                : "flex-1 min-h-[420px] max-h-[560px]"
            }`}
            data-testid="media-panel"
          >
            {uploadIdNum != null ? (
              (() => {
                // For non-overview phases, the user can flip to a stilled frame
                // image. Overview always plays the video. Default behaviour:
                // play the feedback video so coaching overlays are visible.
                const sel = selectedPhase === "overview"
                  ? null
                  : livePositions.find((p) => p.label === selectedPhase);
                const showImage =
                  selectedPhase !== "overview" && viewMode === "image";
                if (showImage) {
                  if (!sel || sel.fileId == null) {
                    return (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                        Frame unavailable
                      </div>
                    );
                  }
                  return (
                    <img
                      key={`${uploadIdNum}-${sel.label}`}
                      src={`${API_BASE}/uploads/getImage?fileIds=${sel.fileId}`}
                      alt={sel.label}
                      className="w-full h-full object-contain bg-black"
                    />
                  );
                }
                const videoSrc =
                  activeFeedbackVideoUrl ?? overviewVideoUrl;
                return (
                  <video
                    ref={videoRef}
                    key={videoSrc}
                    src={videoSrc}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
                    onTimeUpdate={(e) => setVideoTime(e.currentTarget.currentTime)}
                    onError={() => {
                      // Per-feedback video isn't published — fall back to the overview.
                      if (activeFeedbackVideoUrl) setActiveFeedbackVideoUrl(null);
                    }}
                    className={`w-full h-full object-contain bg-black ${mediaTab !== "video" ? "hidden" : ""}`}
                  />
                );
              })()
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-7xl">
                <span className="opacity-90">{session.thumb}</span>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <button className="absolute h-14 w-14 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-lg">
                  <Play className="h-6 w-6 text-el-700" />
                </button>
              </div>
            )}

            {/* Free | Pro | 3D media tabs (lazy 3D viewer via the API endpoints) */}
            {isReal && (
              <div className="absolute top-3 left-3 z-20 inline-flex rounded-md bg-black/40 backdrop-blur-sm ring-1 ring-white/10 overflow-hidden text-[0.65rem] uppercase tracking-[0.08em] font-semibold">
                <button
                  onClick={() => setMediaTab("free")}
                  className={`px-2 py-1 transition ${mediaTab === "free" ? "bg-white text-slate-900" : "text-white/80 hover:text-white"}`}
                >
                  Feedback (Free)
                </button>
                <button
                  onClick={() => setMediaTab("video")}
                  className={`px-2 py-1 transition ${mediaTab === "video" ? "bg-white text-slate-900" : "text-white/80 hover:text-white"}`}
                >
                  Feedback{" "}
                  <span className={mediaTab === "video" ? "text-el-700" : "text-el-400"}>(Pro)</span>
                </button>
                <button
                  onClick={open3dTab}
                  className={`px-2 py-1 transition ${mediaTab === "3d" ? "bg-white text-slate-900" : "text-white/80 hover:text-white"}`}
                >
                  3D
                </button>
              </div>
            )}
            {/* Free-tier feedback: the P-position stills with overlays — feedback on individual
                frames, no rendered video. This is the whole free output surface; Pro adds the
                narrated feedback video. One request lists the stills; the first open of an older
                clip analyses once and writes them (the loader), after that they are file reads. */}
            {isReal && mediaTab === "free" && (
              <div className="absolute inset-0 z-10 flex flex-col bg-black" data-testid="free-feedback-panel">
                {loadingFreeFeedback ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white/80" data-testid="free-feedback-loading">
                    <Loader2 className="h-8 w-8 animate-spin text-el-400" />
                    <div className="text-sm font-medium">Analysing your clip…</div>
                    <div className="text-xs text-white/50">First time only — your positions are being drawn and saved.</div>
                  </div>
                ) : liveReady && session ? (
                  <LiveFreeAnalysis
                    clipUrl={sourceClipUrl(uploadIdNum!)}
                    poseFile={livePoseFile!}
                    moments={liveMoments}
                    activity={session.activity}
                    stillBase={API_BASE}
                    watermarkStamp={String(uploadIdNum)}
                    pageCharts={liveCharts}
                    stageGeometry={freeFeedback?.stage ?? null}
                    splitWindow={freeFeedback?.split ?? null}
                    orderedFrameIds={(uploadFrames?.values ?? []).map((f) => f.fileId)}
                    liveSeries={liveSeries ?? null}
                    cueStills={freeFeedback?.cueStills ?? null}
                    coaching={{
                      summary: analysisSummary,
                      total: totalScoreCard?.value ?? null,
                      rows: overviewFeedbackRows,
                    }}
                  />
                ) : freePositions.length > 0 && freeFrame ? (
                  <>
                    <div className="flex-1 min-h-0 flex items-center justify-center">
                      <img
                        key={freeCue?.key ?? freeFrame.label}
                        src={`${API_BASE}${freeStillUrl}`}
                        alt={freeCue ? `${freeFrame.label} · ${freeCue.description}` : `${freeFrame.label} frame with feedback overlays`}
                        className="max-w-full max-h-full object-contain"
                        data-testid="free-frame-image"
                      />
                    </div>
                    <div className="shrink-0 flex items-center gap-1 overflow-x-auto px-2 py-2 bg-black/60">
                      {freePositions.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => {
                            setFreeFrameLabel(p.label);
                            setFreeCueKey(null); // a new position opens on its first cue
                          }}
                          className={`px-2 py-1 rounded text-[0.65rem] uppercase tracking-[0.06em] font-semibold transition shrink-0 ${
                            freeFrame?.label === p.label
                              ? "bg-white text-slate-900"
                              : "bg-white/10 text-white/80 hover:bg-white/20"
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                      <div className="ml-auto pl-3 pr-1 text-[0.6rem] text-white/50 shrink-0">
                        Frame feedback · Pro adds the narrated video
                      </div>
                    </div>
                    {freeCueRows.length > 0 && (
                      <div
                        className="shrink-0 flex items-center gap-1 overflow-x-auto px-2 pb-2 bg-black/60"
                        data-testid="free-cue-rail"
                      >
                        {freeCueRows.map((r) => (
                          <button
                            key={r.key}
                            type="button"
                            aria-pressed={freeCue?.key === r.key}
                            onClick={() => setFreeCueKey(r.key)}
                            className={`px-2 py-1 rounded text-[0.65rem] font-medium transition shrink-0 ${
                              freeCue?.key === r.key
                                ? "bg-el-500 text-white"
                                : "bg-white/10 text-white/80 hover:bg-white/20"
                            }`}
                          >
                            {r.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-sm text-white/70">
                    No analyzed positions for this upload yet.
                  </div>
                )}
              </div>
            )}
            {/* A free upload has no rendered video: the Pro tab upsells instead of
                showing a dead player. */}
            {isReal && mediaTab === "video" && !hasVideo && hasAnalysis && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black text-white/90" data-testid="pro-upsell-panel">
                {premiumInProgress || upgradeQueued || session?.videoOrderPending ? (
                  <div className="flex max-h-full w-full flex-col items-center gap-3 overflow-y-auto px-4 py-10" data-testid="premium-progress">
                    <div className="text-sm font-medium">Premium is on its way</div>
                    <div className="text-xs text-white/60 text-center">
                      Your on-device result is on the Free tab now. The video appears here by itself when it is ready.
                    </div>
                    <ProcessingJourney journey={journey} tone="dark" />
                    {framesToLabel.length > 0 && !isLabellingComplete && (
                      <div className="w-full max-w-md flex gap-1 overflow-x-auto" data-testid="premium-frame-strip">
                        {framesToLabel.map((f) => (
                          <img
                            key={`${f.fileId}-${f.labelled ? "labelled" : "raw"}`}
                            src={f.labelled ? `${API_BASE}/uploads/getImage?fileIds=${f.fileId}` : frameThumbUrl(f.fileId)}
                            alt={f.labelled ? `frame ${f.fileId} labelled` : `frame ${f.fileId}`}
                            className={`h-12 w-auto shrink-0 rounded bg-black object-contain ${f.labelled ? "" : "opacity-40"}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-medium">The narrated feedback video is a Pro feature</div>
                    <div className="text-xs text-white/60">
                      This upload used the free analysis — your frame feedback is on the Free tab.
                    </div>
                    {canUpgrade && (
                      <button
                        type="button"
                        onClick={onUpgrade}
                        disabled={upgrading}
                        className="mt-2 rounded-md bg-el-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-el-500 disabled:opacity-60"
                      >
                        Upgrade to Premium analysis
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {isReal && mediaTab === "3d" && !has3d && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black text-white/90">
                <Loader2 className="h-8 w-8 animate-spin text-el-400" />
                <div className="text-sm font-medium">Generating 3D model…</div>
                <div className="text-xs text-white/60">This takes a few minutes — it will appear here automatically.</div>
              </div>
            )}
            {threeDActivated && uploadIdNum != null && (
              <iframe
                title="3d-swing-viewer"
                src={`${API_BASE}/uploads/get3dViewer?sfcId=${uploadIdNum}&swing=swing${uploadIdNum}.glb`}
                className={`absolute inset-0 w-full h-full z-10 border-0 bg-black ${mediaTab === "3d" ? "" : "hidden"}`}
              />
            )}

            {/* Video / Frame toggle — only meaningful when a P-position is pinned */}
            {isReal && selectedPhase !== "overview" && (
              <div className="absolute top-3 right-3 inline-flex rounded-md bg-black/40 backdrop-blur-sm ring-1 ring-white/10 overflow-hidden text-[0.65rem] uppercase tracking-[0.08em] font-semibold">
                <button
                  onClick={() => setViewMode("video")}
                  className={`px-2 py-1 transition ${viewMode === "video" ? "bg-white text-slate-900" : "text-white/80 hover:text-white"}`}
                >
                  Video
                </button>
                <button
                  onClick={() => setViewMode("image")}
                  className={`px-2 py-1 transition ${viewMode === "image" ? "bg-white text-slate-900" : "text-white/80 hover:text-white"}`}
                >
                  Frame
                </button>
              </div>
            )}
            <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white pointer-events-none">
              <div className="text-sm">
                <div className="font-medium">{session.athlete}</div>
                <div className="text-xs opacity-80">{ACTIVITY_LABEL[session.activity]}</div>
              </div>
              <div className="text-xs opacity-80 tabular-nums">
                {isReal ? (
                  selectedPhase !== "overview" && viewMode === "image" ? (() => {
                    const sel = livePositions.find((p) => p.label === selectedPhase);
                    return sel ? `${sel.label} · frame ${sel.fileId}` : selectedPhase;
                  })() : (
                    <>
                      {selectedPhase !== "overview" ? `${selectedPhase} · ` : ""}
                      {fmtClock(videoTime)}
                      {videoDuration > 0 ? ` / ${fmtClock(videoDuration)}` : ""}
                    </>
                  )
                ) : (
                  `${formatTime(session.capturedAt)} · ${session.durationSec}s`
                )}
              </div>
            </div>
          </div>
        </div>

        {/* coaching feedback panel — Total, AI summary, and feedback list */}
        <div
          className={`card p-5 flex flex-col lg:h-full lg:max-h-[560px] min-h-0 overflow-hidden ${liveOpen ? "hidden" : ""}`}
          data-testid="coaching-card"
        >
          <div className="text-sm font-semibold text-slate-900 mb-3 shrink-0">
            Coaching feedback
          </div>

          {isReal ? (
            metricsError && !totalScoreCard && liveMetricCards.length === 0 ? (
              <RetryBlock label="Couldn't load metrics" onRetry={reloadMetrics} />
            ) : loadingMetrics && liveMetricCards.length === 0 && !totalScoreCard ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-el-600" /> Loading…
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="shrink-0 space-y-4">
                  {totalScoreCard && (
                    <LiveMeasure
                      card={{ ...totalScoreCard, label: "Total" }}
                      active={selectedPhase === "overview"}
                      align="center"
                      onClick={() => {
                        jumpToPhase("overview", null);
                        const v = videoRef.current;
                        if (v) {
                          v.currentTime = 0;
                          v.play().catch(() => {});
                        }
                      }}
                    />
                  )}
                  {/* Derived from the REAL scores + coaching rows on this page — replaces a
                      hardcoded paragraph that showed the same text on every session. */}
                  {analysisSummary && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Sparkles className="h-4 w-4 text-el-600" />
                        <div className="text-sm font-semibold text-slate-900">Summary</div>
                      </div>
                      <p className="text-xs leading-relaxed text-slate-600" data-testid="analysis-summary">
                        {analysisSummary}
                      </p>
                    </div>
                  )}
                </div>
                {coachingMode === "all" && (
                  <div className="flex-1 min-h-0 flex flex-col mt-4">
                    <div className="text-xs text-slate-500 mb-2 shrink-0">
                      {overlaysLoading
                        ? "Loading feedback…"
                        : overviewFeedbackRows.length > 0
                        ? `${overviewFeedbackRows.length} measure${overviewFeedbackRows.length === 1 ? "" : "s"} across ${new Set(overviewFeedbackRows.map((r) => r.pLabel)).size} phase(s) · click to scrub`
                        : ""}
                    </div>
                    {overlaysError ? (
                      <RetryBlock label="Couldn't load coaching feedback" onRetry={reloadOverlays} />
                    ) : overlaysLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-el-600" />
                      </div>
                    ) : overviewFeedbackRows.length > 0 ? (
                      <ul className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
                        {overviewFeedbackRows.map((r, i) => {
                          const rowKey = `${r.pLabel}-${r.overlay}-${i}`;
                          const isActive = activeCoachingKey === rowKey;
                          return (
                            <li key={rowKey}>
                              <button
                                type="button"
                                onClick={() => {
                                  const url = overlayBundle?.feedbackVideos?.[r.overlay] ?? null;
                                  setActiveCoachingKey(rowKey);
                                  jumpToPhase(r.pLabel, r.fileId, {
                                    keepCoachingList: true,
                                    feedbackVideoUrl: url,
                                    overlay: r.overlay,
                                  });
                                }}
                                className={`w-full text-left rounded-md transition px-3 py-2 flex items-baseline justify-between gap-3 border ${
                                  isActive
                                    ? "bg-el-50 border-el-300 ring-1 ring-el-200"
                                    : "bg-slate-50 border-slate-100 hover:border-el-200"
                                }`}
                              >
                                <div className="flex items-baseline gap-2 min-w-0">
                                  <span className="text-[0.65rem] font-semibold text-el-700 shrink-0 tabular-nums">
                                    {r.pLabel}
                                  </span>
                                  <div className="min-w-0">
                                    <div className={`text-xs truncate ${isActive ? "font-semibold text-el-800" : "font-medium text-slate-800"}`}>
                                      {r.description}
                                    </div>
                                    {r.subText && (
                                      <div className="text-[0.6rem] text-slate-400 truncate">{r.subText}</div>
                                    )}
                                  </div>
                                </div>
                                {r.values.length > 0 && (
                                  <div className="flex shrink-0 gap-1">
                                    {r.values.map((v) => (
                                      <span
                                        key={v}
                                        className={`rounded px-1.5 py-0.5 text-[0.6rem] font-semibold tabular-nums ${
                                          isActive ? "bg-el-100 text-el-800" : "bg-white text-slate-600"
                                        }`}
                                      >
                                        {v}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <RetryBlock
                        label="No coaching feedback for this upload yet"
                        onRetry={reloadOverlays}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Measure label="Spine angle"        value={session.metrics.spineAngle}       unit="°" />
              <Measure label="Hip rotation"       value={session.metrics.hipRotation}      unit="°" />
              <Measure label="Shoulder rotation"  value={session.metrics.shoulderRotation} unit="°" />
              <Measure label="Pelvic tilt"        value={session.metrics.pelvicTilt}       unit="°" />
              <Measure label="Knee flexion"       value={session.metrics.kneeFlexion}      unit="°" />
              <Measure label="Lead-arm straightness" value={session.metrics.armStraightness * 100} unit="%" precision={0} />
            </div>
          )}
        </div>
      </div>

      {/* body measures — P-position scores grid */}
      {isReal && (liveMetricCards.length > 0 || totalScoreCard || metricsError || loadingMetrics) && (
        <div className="card p-5 space-y-3">
          <div className="text-sm font-semibold text-slate-900">Body measures</div>
          {metricsError && liveMetricCards.length === 0 && !totalScoreCard ? (
            <RetryBlock label="Couldn't load body measures" onRetry={reloadMetrics} />
          ) : loadingMetrics && liveMetricCards.length === 0 && !totalScoreCard ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-el-600" />
            </div>
          ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {totalScoreCard && (
              <LiveMeasure
                card={{ ...totalScoreCard, label: "Total" }}
                active={selectedPhase === "overview"}
                onClick={() => {
                  setSelectedPhase("overview");
                  setPhase("overview");
                }}
              />
            )}
            {liveMetricCards.map((m, i) => {
              const match = /^P(\d+)\b/i.exec(m.label.trim());
              const phaseLabel = match ? `P${match[1]}` : null;
              return (
                <LiveMeasure
                  key={`${m.label}-${i}`}
                  card={m}
                  active={phaseLabel != null && selectedPhase === phaseLabel}
                  onClick={
                    phaseLabel
                      ? () => {
                          const fid =
                            livePositions.find((p) => p.label === phaseLabel)?.fileId ?? null;
                          jumpToPhase(phaseLabel, fid);
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
          )}
        </div>
      )}

      {isReal && coachingMode === "phase" && selectedPhase !== "overview" && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-slate-900">
                  Coaching feedback · {selectedPhase}
                </div>
                <button
                  type="button"
                  onClick={() => setCoachingMode("all")}
                  className="text-[0.65rem] uppercase tracking-[0.06em] font-semibold text-el-700 hover:text-el-800"
                >
                  ← All phases
                </button>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {overlaysLoading
                  ? "Loading frame feedback…"
                  : overlayFeedbackRows.length > 0
                  ? `${overlayFeedbackRows.length} measure${overlayFeedbackRows.length === 1 ? "" : "s"} · from rendered overlays`
                  : "No feedback emitted for this frame yet"}
              </div>
            </div>
            {selectedFileId != null && (
              <div className="text-[0.65rem] text-slate-400 tabular-nums shrink-0">frame {selectedFileId}</div>
            )}
          </div>
          {overlaysLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-el-600" /> Loading feedback…
            </div>
          ) : overlayFeedbackRows.length > 0 ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {overlayFeedbackRows.map((r, i) => (
                <li
                  key={`${r.overlay}-${i}`}
                  className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 flex items-baseline justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">
                      {r.description}
                    </div>
                    <div className="text-[0.6rem] text-slate-400 truncate">{r.overlay}</div>
                  </div>
                  {r.value && (
                    <div className="text-sm font-semibold text-slate-900 tabular-nums shrink-0">
                      {r.value}
                      <span className="text-[0.65rem] text-slate-500 ml-0.5">{r.units}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-slate-500 italic">
              The renderer hasn't emitted overlay JSONs for this frame. They land in the same
              folder as the rendered video — re-run the feedback video to populate them.
            </div>
          )}
        </div>
      )}

      {/* phase timeline */}
      {isReal && livePositions.length > 0 && (() => {
        const tlPoints: { id: string; label: string }[] = [
          ...livePositions.map((p) => ({ id: p.label, label: p.label })),
          { id: "overview", label: "Total" },
        ];
        return (
          <PhaseTimeline
            active={selectedPhase}
            onSelect={(id) => {
              const fid = livePositions.find((p) => p.label === id)?.fileId ?? null;
              jumpToPhase(id, fid);
            }}
            positions={tlPoints.map((p, i) => ({
              id: p.id,
              label: p.label,
              pct:
                tlPoints.length === 1
                  ? 0
                  : (i / (tlPoints.length - 1)) * 100,
            }))}
            subtitle={`P1 → P${livePositions.length} · Total`}
            activity={session?.activity}
          />
        );
      })()}
      {!isReal && isGolf && <PhaseTimeline active={phase} onSelect={setPhase} />}

      {/* angle chart — real uploads use /metrics/appAnglesTab */}
      {isReal ? (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Joint angles per frame</div>
              <div className="text-xs text-slate-500">
                {loadingAngles
                  ? "Loading from /metrics/appAnglesTab…"
                  : liveCharts.length > 0
                  ? `${liveCharts.length} metric${liveCharts.length === 1 ? "" : "s"} from EL-Metrics pipeline`
                  : "No chart data returned for this upload"}
              </div>
            </div>
          </div>
          {loadingAngles ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-el-600" /> Loading charts…
            </div>
          ) : anglesError ? (
            <RetryBlock
              label={`Couldn't load charts: ${anglesError.message}`}
              onRetry={reloadAngles}
            />
          ) : liveCharts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {liveCharts.map((c, i) => (
                <LiveAngleChart
                  key={`${c.title}-${i}`}
                  chart={c}
                  index={i}
                  phases={livePositions.map((p, j) => ({
                    label: p.label,
                    pct:
                      livePositions.length === 1
                        ? 0
                        : (j / (livePositions.length - 1)) * 100,
                  }))}
                  activePhase={selectedPhase}
                />
              ))}
            </div>
          ) : (
            <RetryBlock
              label="No chart data returned for this upload"
              onRetry={reloadAngles}
            />
          )}
        </div>
      ) : (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Joint angles per frame</div>
              <div className="text-xs text-slate-500">
                {isGolf ? `Phase markers P1–P10 — selected: ${phase}` : "60 frames captured"}
              </div>
            </div>
            <div className="text-xs text-slate-500">From EL-Metrics pipeline</div>
          </div>
          <AngleChart active={isGolf ? phase : undefined} />
        </div>
      )}

      <ShareSidebar
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={shareUrl}
        title={`Eleetz session ${session.id}`}
        copied={copied}
        onCopy={copyLink}
      />

      {uploadIdNum != null && (
        <SubjectPickerDialog
          open={tagPickerOpen}
          onClose={() => setTagPickerOpen(false)}
          uploadId={uploadIdNum}
          apiFetch={apiFetch}
          onTagged={onSubjectTagged}
        />
      )}

      {/* Change technique: the SAME picker the upload screen uses (owner: "reuse the current
          sport list frontend we have on the upload screen"), narrowed to the techniques this
          clip's frames can serve. Choosing one re-analyses the upload as that technique. */}
      {techniqueMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setTechniqueMenuOpen(false)} aria-hidden />
          <div
            className="fixed left-1/2 top-16 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl card p-5 shadow-2xl max-h-[80vh] overflow-y-auto"
            role="dialog"
            aria-label="Change technique"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-el-600" />
                <div className="text-sm font-semibold text-slate-900">Change technique</div>
              </div>
              <button onClick={() => setTechniqueMenuOpen(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            {currentSport && (
              <div
                data-testid="technique-current"
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 mb-3"
              >
                <span className="text-lg">{currentSport.emoji}</span>
                <span className="text-xs text-slate-500">
                  Currently set to{" "}
                  <span className="font-medium text-slate-800">{currentSport.label}</span>
                </span>
              </div>
            )}
            <p className="text-xs text-slate-500 mb-3">
              The clip is re-analysed as the technique you pick. Its frames were sampled for{" "}
              {currentSportId != null && NATIVE_RATE_SPORT_IDS.has(currentSportId)
                ? "a movement technique, at the clip's own frame rate"
                : "a fixed-cadence technique, every 100 ms"}
              , so techniques that need the other sampling are shown but cannot be chosen.
            </p>
            <SportPicker
              options={techniqueOptions}
              selectedId={currentSportId}
              onSelect={onChangeTechnique}
              disabled={techniqueBusy}
              testIdPrefix="technique-option"
            />
          </div>
        </>
      )}

      {uploadIdNum != null && (
        <EventPickerDialog
          open={eventDialogOpen}
          onClose={() => setEventDialogOpen(false)}
          uploadId={uploadIdNum}
          apiFetch={apiFetch}
          initialName={currentEventName ?? ""}
          initialDate={currentEventDate ?? ""}
          onTagged={onEventTagged}
        />
      )}

      {uploadIdNum != null && (
        <EquipmentDialog
          open={equipmentDialogOpen}
          onClose={() => setEquipmentDialogOpen(false)}
          uploadId={uploadIdNum}
          apiFetch={apiFetch}
          initialType={currentEquipment.type ?? ""}
          initialBrand={currentEquipment.brand ?? ""}
          initialSpec={currentEquipment.spec ?? ""}
          onTagged={onEquipmentTagged}
        />
      )}
    </div>
  );
}

/**
 * Picker dialog for tagging the upload's subject: debounced name search over
 * platform/contactsSearch (NULL-org pros/athletes AND user contacts), select-to-tag via
 * uploadSetSubject, and a create-new-athlete affordance (platform/athleteCreate,
 * find-or-create server-side) that appears only once a finished search has no exact match.
 */
function SubjectPickerDialog({
  open,
  onClose,
  uploadId,
  apiFetch,
  onTagged,
}: {
  open: boolean;
  onClose: () => void;
  uploadId: number;
  apiFetch: ReturnType<typeof useApi>;
  onTagged: (contactId: number, name: string) => void;
}) {
  const [q, setQ] = useState("");
  // null = no completed search for the current term yet (gates the create affordance).
  const [results, setResults] = useState<ContactSearchItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset per open so a re-opened picker starts clean.
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setError(null);
    }
  }, [open]);

  // Debounced search. 300ms is a UI responsiveness choice, not a derived threshold.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await searchContacts(apiFetch, term);
        if (!cancelled) {
          setResults(resp?.values ?? []);
          setError(null);
        }
      } catch {
        if (!cancelled) setError("Search failed — try again.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, open, apiFetch]);

  const tag = useCallback(
    async (contactId: number, name: string, linkedExisting?: boolean) => {
      setBusy(true);
      setError(null);
      try {
        await setUploadSubject(apiFetch, uploadId, contactId);
        onTagged(contactId, name);
        if (linkedExisting) {
          window.alert(`Linked to existing athlete "${name}".`);
        }
        onClose();
      } catch {
        setError("Couldn't tag the athlete — try again.");
      } finally {
        setBusy(false);
      }
    },
    [apiFetch, uploadId, onTagged, onClose],
  );

  const onCreate = useCallback(async () => {
    const name = q.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createAthlete(apiFetch, name);
      await tag(created.contactId, created.name ?? name, created.existing);
    } catch {
      setError("Couldn't create the athlete — try again.");
      setBusy(false);
    }
  }, [q, busy, apiFetch, tag]);

  if (!open) return null;

  const term = q.trim();
  const exactMatch =
    results?.some((r) => r.name.trim().toLowerCase() === term.toLowerCase()) ?? false;
  // Create only when a search for the current term has COMPLETED without an exact match —
  // search-before-create keeps athlete identity from fragmenting into duplicates.
  const showCreate = term.length > 0 && results != null && !searching && !exactMatch;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} aria-hidden />
      <div
        className="fixed left-1/2 top-24 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md card p-5 shadow-2xl"
        role="dialog"
        aria-label="Tag athlete"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Tag athlete</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search athletes and users…"
            className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
          />
        </div>
        <div className="text-[0.7rem] text-slate-500 mt-1.5">
          Who is in this video? Search platform athletes and users, or create a new athlete.
        </div>

        <div className="mt-3 max-h-64 overflow-y-auto space-y-1">
          {searching && (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2 justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-el-600" /> Searching…
            </div>
          )}
          {!searching &&
            (results ?? []).map((r) => (
              <button
                key={r.contactId}
                type="button"
                disabled={busy}
                onClick={() => tag(r.contactId, r.name)}
                className="w-full text-left rounded-md transition px-3 py-2 flex items-center gap-2 border bg-slate-50 border-slate-100 hover:border-el-200 disabled:opacity-50"
              >
                <UserRound className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-800 truncate">{r.name}</span>
              </button>
            ))}
          {!searching && results != null && results.length === 0 && (
            <div className="text-xs text-slate-500 italic py-2 text-center">
              No matching contacts.
            </div>
          )}
          {showCreate && (
            <button
              type="button"
              disabled={busy}
              onClick={onCreate}
              className="w-full text-left rounded-md transition px-3 py-2 flex items-center gap-2 border border-dashed border-el-300 bg-el-50/50 hover:bg-el-50 disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin text-el-600 shrink-0" />
              ) : (
                <UserPlus className="h-4 w-4 text-el-600 shrink-0" />
              )}
              <span className="text-sm font-medium text-el-800 truncate">
                Create athlete "{term}"
              </span>
            </button>
          )}
        </div>

        {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      </div>
    </>
  );
}

/**
 * Event tag dialog: name (with debounced suggestions from platform/eventsSearch — the
 * emergent canonical list, so "Masters 2026" vs "The Masters 2026" doesn't fragment),
 * plus an optional ISO date. Saving an EMPTY name clears the tag; the save always sets
 * the full event state (name without date clears a stale date server-side).
 */
function EventPickerDialog({
  open,
  onClose,
  uploadId,
  apiFetch,
  initialName,
  initialDate,
  onTagged,
}: {
  open: boolean;
  onClose: () => void;
  uploadId: number;
  apiFetch: ReturnType<typeof useApi>;
  initialName: string;
  initialDate: string;
  onTagged: (name: string | null, date: string | null) => void;
}) {
  const [name, setName] = useState(initialName);
  const [date, setDate] = useState(initialDate);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suppressSearchRef = useRef(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDate(initialDate);
      setSuggestions([]);
      setError(null);
      suppressSearchRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced suggestions - a blank term also queries (the backend returns top
  // suggestions picker-first). 300ms is a UI responsiveness choice.
  useEffect(() => {
    if (!open) return;
    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await searchEvents(apiFetch, name.trim());
        if (!cancelled) setSuggestions(resp?.values ?? []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [name, open, apiFetch]);

  const onSave = useCallback(async () => {
    if (busy) return;
    const n = name.trim();
    const d = n === "" ? "" : date.trim();
    setBusy(true);
    setError(null);
    try {
      await setUploadEvent(apiFetch, uploadId, n, d);
      onTagged(n === "" ? null : n, d === "" ? null : d);
      onClose();
    } catch {
      setError("Couldn't save the event — try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, name, date, apiFetch, uploadId, onTagged, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} aria-hidden />
      <div
        className="fixed left-1/2 top-24 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md card p-5 shadow-2xl"
        role="dialog"
        aria-label="Tag event"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Tag event</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Event name…"
          className="w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
        />
        <div className="text-[0.7rem] text-slate-500 mt-1.5">
          Which tournament/competition is this clip from? Pick an existing event if it's
          listed — one spelling per event keeps results linkable. Leave empty to clear.
        </div>

        {suggestions.length > 0 && (
          <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                disabled={busy}
                onClick={() => {
                  suppressSearchRef.current = true;
                  setName(s);
                  setSuggestions([]);
                }}
                className="w-full text-left rounded-md transition px-3 py-1.5 text-sm text-slate-800 border bg-slate-50 border-slate-100 hover:border-el-200 disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <label className="text-xs text-slate-500">Date (optional):</label>
          <input
            type="date"
            data-testid="event-date-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-8 px-2 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onSave} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save event
          </button>
        </div>

        {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      </div>
    </>
  );
}

/**
 * Equipment tag dialog: three independent free-text fields (unstructured v1 — no catalog
 * table). Type and brand get debounced DISTINCT suggestions from platform/equipmentSuggest
 * (picker-first anti-fragmentation, like events); spec is deliberately suggestion-free.
 * A blank field clears that row server-side.
 */
function EquipmentDialog({
  open,
  onClose,
  uploadId,
  apiFetch,
  initialType,
  initialBrand,
  initialSpec,
  onTagged,
}: {
  open: boolean;
  onClose: () => void;
  uploadId: number;
  apiFetch: ReturnType<typeof useApi>;
  initialType: string;
  initialBrand: string;
  initialSpec: string;
  onTagged: (type: string | null, brand: string | null, spec: string | null) => void;
}) {
  const [type, setType] = useState(initialType);
  const [brand, setBrand] = useState(initialBrand);
  const [spec, setSpec] = useState(initialSpec);
  const [typeSuggestions, setTypeSuggestions] = useState<string[]>([]);
  const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suppressTypeRef = useRef(false);
  const suppressBrandRef = useRef(false);

  useEffect(() => {
    if (open) {
      setType(initialType);
      setBrand(initialBrand);
      setSpec(initialSpec);
      setTypeSuggestions([]);
      setBrandSuggestions([]);
      setError(null);
      suppressTypeRef.current = false;
      suppressBrandRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced type/brand suggestions (300ms - UI responsiveness choice).
  useEffect(() => {
    if (!open) return;
    if (suppressTypeRef.current) {
      suppressTypeRef.current = false;
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await suggestEquipment(apiFetch, "type", type.trim());
        if (!cancelled) setTypeSuggestions(resp?.values ?? []);
      } catch {
        if (!cancelled) setTypeSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [type, open, apiFetch]);

  useEffect(() => {
    if (!open) return;
    if (suppressBrandRef.current) {
      suppressBrandRef.current = false;
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await suggestEquipment(apiFetch, "brand", brand.trim());
        if (!cancelled) setBrandSuggestions(resp?.values ?? []);
      } catch {
        if (!cancelled) setBrandSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [brand, open, apiFetch]);

  const onSave = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const t = type.trim();
    const b = brand.trim();
    const s = spec.trim();
    try {
      await setUploadEquipment(apiFetch, uploadId, { type: t, brand: b, spec: s });
      onTagged(t === "" ? null : t, b === "" ? null : b, s === "" ? null : s);
      onClose();
    } catch {
      setError("Couldn't save the equipment — try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, type, brand, spec, apiFetch, uploadId, onTagged, onClose]);

  if (!open) return null;

  const suggestionList = (
    items: string[],
    onPick: (value: string) => void,
  ) =>
    items.length > 0 && (
      <div className="mt-1 max-h-28 overflow-y-auto space-y-1">
        {items.map((s) => (
          <button
            key={s}
            type="button"
            disabled={busy}
            onClick={() => onPick(s)}
            className="w-full text-left rounded-md transition px-3 py-1.5 text-sm text-slate-800 border bg-slate-50 border-slate-100 hover:border-el-200 disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>
    );

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} aria-hidden />
      <div
        className="fixed left-1/2 top-24 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md card p-5 shadow-2xl"
        role="dialog"
        aria-label="Tag equipment"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Tag equipment</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <input
              autoFocus
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="Type — e.g. 7 iron, racket…"
              className="w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
            {suggestionList(typeSuggestions, (s) => {
              suppressTypeRef.current = true;
              setType(s);
              setTypeSuggestions([]);
            })}
          </div>
          <div>
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="Brand — e.g. Titleist…"
              className="w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
            {suggestionList(brandSuggestions, (s) => {
              suppressBrandRef.current = true;
              setBrand(s);
              setBrandSuggestions([]);
            })}
          </div>
          <div>
            <input
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
              placeholder="Spec — e.g. stiff shaft…"
              className="w-full h-9 px-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
            <div className="text-[0.7rem] text-slate-500 mt-1">
              Free text — shaft flex, string tension, anything that affects the movement.
              Blank fields clear their tag.
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onSave} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save equipment
          </button>
        </div>

        {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      </div>
    </>
  );
}

/**
 * Free-form user tags as an inline chips row ("new grip", "windy", "lesson with
 * coach"). Every add/remove saves the WHOLE set via uploadSetUserTags (the backend
 * replaces its single "|"-delimited row), then resyncs through onSaved. Suggestions
 * while typing come from userTagsSuggest — the caller's OWN tags only (personal
 * vocabulary, enforced server-side). Over-cap sets are rejected by the server, never
 * truncated, so a failed save leaves the chips untouched.
 */
function UserTagsRow({
  uploadId,
  apiFetch,
  tags,
  onSaved,
}: {
  uploadId: number;
  apiFetch: ReturnType<typeof useApi>;
  tags: string[];
  onSaved: (tags: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const suppressRef = useRef(false);

  // Debounced own-tags suggestions (300ms, matching the sibling pickers). Blank
  // draft suggests too (picker-first) — but only while the input has focus, so the
  // dropdown doesn't sit open permanently under the row.
  useEffect(() => {
    const term = draft.trim();
    if (!focused && term.length === 0) {
      setSuggestions([]);
      return;
    }
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await suggestUserTags(apiFetch, term);
        if (!cancelled) setSuggestions(resp?.values ?? []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [draft, focused, apiFetch]);

  const save = useCallback(
    async (next: string[]) => {
      if (busy) return;
      setBusy(true);
      try {
        await setUploadUserTags(apiFetch, uploadId, next);
        onSaved(next);
      } catch {
        // Includes the server's over-cap rejection: nothing was written, so the
        // chips deliberately stay as they were.
        window.alert("Couldn't save the tags. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [busy, apiFetch, uploadId, onSaved],
  );

  const addTag = useCallback(
    (raw: string) => {
      const tag = raw.trim().replace(/\s+/g, " ");
      setDraft("");
      setSuggestions([]);
      suppressRef.current = true;
      if (tag === "" || tags.some((t) => t.toLowerCase() === tag.toLowerCase())) return;
      void save([...tags, tag]);
    },
    [tags, save],
  );

  const visibleSuggestions = suggestions.filter(
    (s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase()),
  );

  return (
    <span className="inline-flex items-center gap-1.5 relative">
      <Tag className="h-3.5 w-3.5 text-slate-400" />
      <span>Tags:</span>
      <span className="inline-flex items-center gap-1 flex-wrap" data-testid="user-tags-display">
        {tags.map((tag) => (
          <span
            key={tag}
            data-testid="user-tag-chip"
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {tag}
            <button
              type="button"
              disabled={busy}
              onClick={() => save(tags.filter((t) => t !== tag))}
              title={`Remove tag "${tag}"`}
              className="p-0.5 -mr-1 rounded-full hover:bg-slate-200 transition disabled:opacity-50"
            >
              <X className="h-3 w-3 text-slate-500" />
            </button>
          </span>
        ))}
      </span>
      <span className="relative">
        <input
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag(draft);
            }
          }}
          placeholder="Add tag…"
          className="h-6 w-28 px-2 text-xs rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring disabled:opacity-50"
        />
        {visibleSuggestions.length > 0 && (
          <div className="absolute left-0 top-7 z-30 w-48 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg p-1 space-y-0.5">
            {visibleSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                disabled={busy}
                // onMouseDown beats the input's blur, so a click always lands.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(s)}
                className="w-full text-left rounded px-2 py-1 text-xs text-slate-800 hover:bg-slate-50 disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </span>
    </span>
  );
}

function Measure({
  label,
  value,
  unit,
  precision = 1,
}: {
  label: string;
  value: number;
  unit: string;
  precision?: number;
}) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2.5 border border-slate-100">
      <div className="text-[0.65rem] uppercase tracking-[0.06em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <div className="text-base font-semibold text-slate-900 tracking-tightish">
          {value.toFixed(precision)}
        </div>
        <div className="text-[0.7rem] text-slate-500">{unit}</div>
      </div>
    </div>
  );
}

function LiveMeasure({
  card,
  active = false,
  onClick,
  align = "left",
}: {
  card: MetricCardData;
  active?: boolean;
  onClick?: () => void;
  align?: "left" | "center";
}) {
  const cls = [
    "rounded-md px-3 py-2.5 border transition w-full",
    align === "center" ? "text-center" : "text-left",
    active
      ? "bg-el-50 border-el-200 ring-1 ring-el-200"
      : "bg-slate-50 border-slate-100",
    onClick ? "hover:border-el-200 cursor-pointer" : "",
  ].join(" ");
  const inner = (
    <>
      <div className="text-[0.65rem] uppercase tracking-[0.06em] text-slate-500 font-semibold">
        {card.label}
      </div>
      <div className="mt-0.5 text-base font-semibold text-slate-900 tracking-tightish">
        {card.value}
      </div>
      {card.grouping && (
        <div className="text-[0.6rem] text-slate-400 mt-0.5">{card.grouping}</div>
      )}
    </>
  );
  return onClick ? (
    <button type="button" className={cls} onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function ShareSidebar({
  open,
  onClose,
  url,
  title,
  copied,
  onCopy,
}: {
  open: boolean;
  onClose: () => void;
  url: string;
  title: string;
  copied: boolean;
  onCopy: () => void;
}) {
  if (!open) return null;
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(`Take a look at this session on Eleetz:\n\n${url}`);
  const mailto = `mailto:?subject=${subject}&body=${body}`;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[400px] bg-white shadow-2xl border-l border-slate-200 flex flex-col"
        role="dialog"
        aria-label="Share session"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-el-600" />
            <div className="text-sm font-semibold text-slate-900">Share session</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <div className="label-eyebrow mb-2">Link</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 truncate">
                {url}
              </div>
              <button
                onClick={onCopy}
                className="btn-secondary !h-9 shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-600" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy
                  </>
                )}
              </button>
            </div>
            <div className="text-[0.7rem] text-slate-500 mt-1.5">
              Anyone with the link who can sign in to your workspace can view this session.
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Send</div>
            <div className="grid grid-cols-1 gap-2">
              <a
                href={mailto}
                className="rounded-md border border-slate-200 hover:border-el-300 transition px-3 py-2.5 flex items-center gap-3"
              >
                <Mail className="h-4 w-4 text-el-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">Email</div>
                  <div className="text-[0.7rem] text-slate-500">
                    Open your mail client with the link prefilled
                  </div>
                </div>
              </a>
              <button
                onClick={onCopy}
                className="rounded-md border border-slate-200 hover:border-el-300 transition px-3 py-2.5 flex items-center gap-3 text-left"
              >
                <LinkIcon className="h-4 w-4 text-el-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">Copy link</div>
                  <div className="text-[0.7rem] text-slate-500">
                    Paste anywhere — Slack, DMs, docs.
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="rounded-md bg-amber-50 ring-1 ring-amber-100 px-3 py-2 text-[0.7rem] text-amber-800">
            Visibility controls (private / friends / org / public) are coming soon. For now this link
            requires the recipient to have access to the same workspace.
          </div>
        </div>
      </aside>
    </>
  );
}

function ScopePill({ scope }: { scope: ShareScope }) {
  const current = SHARE_SCOPES.find((s) => s.id === scope) ?? SHARE_SCOPES[0];
  return (
    <button
      type="button"
      disabled
      className="btn-secondary !h-9 opacity-50 cursor-not-allowed"
      title="Visibility controls coming soon"
    >
      <Sparkles className="h-3.5 w-3.5 text-el-600" />
      Visible to: <span className="font-semibold ml-1">{current.label}</span>
    </button>
  );
}
